import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    TextField,
    Box,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
    fetchFreezeBlockReport,
    setFreezeBlockFilters,
    clearFreezeBlockReport,
} from '../../../store/actions/customerReportActions';

const FreezeBlockReport = () => {
    const dispatch = useDispatch();
    const { auth, customerReport } = useSelector((state) => state);
    const { market, category, brand } = auth.user;
    const { freezeBlockFilters, freezeBlockReport, totalCount, hasSearched } =
        customerReport;

    const [formError, setFormError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleFilterChange = (field, value) => {
        dispatch(
            setFreezeBlockFilters({
                [field]: value,
            })
        );
    };

    const handleClear = () => {
        dispatch(clearFreezeBlockReport());
        setFormError('');
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);

        const start = newPage * rowsPerPage;
        dispatch(
            fetchFreezeBlockReport(
                market,
                category,
                brand,
                dayjs(freezeBlockFilters.startDate)
                    .startOf('day')
                    .toISOString(),
                dayjs(freezeBlockFilters.endDate).endOf('day').toISOString(),
                freezeBlockFilters.queryByField === 'gigyaId'
                    ? freezeBlockFilters.queryByValue
                    : '',
                freezeBlockFilters.promotionType,
                start,
                rowsPerPage
            )
        );
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);

        dispatch(
            fetchFreezeBlockReport(
                market,
                category,
                brand,
                dayjs(freezeBlockFilters.startDate)
                    .startOf('day')
                    .toISOString(),
                dayjs(freezeBlockFilters.endDate).endOf('day').toISOString(),
                freezeBlockFilters.queryByField === 'gigyaId'
                    ? freezeBlockFilters.queryByValue
                    : '',
                freezeBlockFilters.promotionType,
                0,
                newRowsPerPage
            )
        );
    };

    const handleSubmit = () => {
        setFormError('');

        if (!freezeBlockFilters.startDate || !freezeBlockFilters.endDate) {
            setFormError('Please select both start and end dates.');
            return;
        }
        if (freezeBlockFilters.startDate > freezeBlockFilters.endDate) {
            setFormError('Start date cannot be after end date.');
            return;
        }

        setPage(0);

        dispatch(
            fetchFreezeBlockReport(
                market,
                category,
                brand,
                dayjs(freezeBlockFilters.startDate)
                    .startOf('day')
                    .toISOString(),
                dayjs(freezeBlockFilters.endDate).endOf('day').toISOString(),
                freezeBlockFilters.queryByField === 'gigyaId'
                    ? freezeBlockFilters.queryByValue
                    : '',
                freezeBlockFilters.promotionType,
                0,
                rowsPerPage
            )
        );
    };

    const getBlockTypeColor = (blockType) => {
        if (blockType?.toLowerCase() === 'freeze') {
            return { backgroundColor: '#FFF4E5', color: '#FF9800' };
        } else if (blockType?.toLowerCase() === 'block') {
            return { backgroundColor: '#FFEBEE', color: '#F44336' };
        }
        return { backgroundColor: '#E0E0E0', color: '#616161' };
    };

    return (
        <div className="customer-report-child">
            <h3 className="customer-report-child-title">
                Search Transaction Criteria
            </h3>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 3,
                    maxWidth: '600px',
                }}
            >
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <DatePicker
                        label={<Box color={'#000'}>From*</Box>}
                        value={freezeBlockFilters.startDate}
                        onChange={(date) =>
                            handleFilterChange('startDate', date)
                        }
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: { flex: 1 },
                            },
                        }}
                    />
                    <DatePicker
                        label={<Box color={'#000'}>To*</Box>}
                        value={freezeBlockFilters.endDate}
                        onChange={(date) => handleFilterChange('endDate', date)}
                        minDate={freezeBlockFilters.startDate}
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: { flex: 1 },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel id="query-by-field-label">
                            Query By
                        </InputLabel>
                        <Select
                            labelId="query-by-field-label"
                            value={freezeBlockFilters.queryByField}
                            label="Query By"
                            onChange={(e) =>
                                handleFilterChange(
                                    'queryByField',
                                    e.target.value
                                )
                            }
                            disabled
                        >
                            <MenuItem value="gigyaId">Gigya ID</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label={
                            freezeBlockFilters.queryByField === 'gigyaId'
                                ? 'Gigya ID'
                                : 'Value'
                        }
                        value={freezeBlockFilters.queryByValue}
                        onChange={(e) =>
                            handleFilterChange('queryByValue', e.target.value)
                        }
                        placeholder={
                            freezeBlockFilters.queryByField === 'gigyaId'
                                ? 'Enter Gigya ID'
                                : 'Enter Value'
                        }
                        size="small"
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="type-select-label">
                            Promotion Type
                        </InputLabel>
                        <Select
                            labelId="type-select-label"
                            value={freezeBlockFilters.promotionType}
                            label="Promotion Type"
                            onChange={(e) =>
                                handleFilterChange(
                                    'promotionType',
                                    e.target.value
                                )
                            }
                        >
                            <MenuItem value="GOLDEN_TICKET">
                                Golden Ticket
                            </MenuItem>
                            <MenuItem value="UPC">UPC</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {formError && (
                    <p className="customer-report-child-error">{formError}</p>
                )}

                <Box
                    sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        sx={{ minWidth: '120px' }}
                    >
                        Reset query
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            !freezeBlockFilters.startDate ||
                            !freezeBlockFilters.endDate ||
                            freezeBlockFilters.startDate >
                                freezeBlockFilters.endDate
                        }
                        sx={{ minWidth: '120px' }}
                    >
                        Search
                    </Button>
                </Box>
            </Box>

            {!hasSearched && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '300px',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: '#E8F5E9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{ color: '#666', fontWeight: 500 }}
                    >
                        Search for Transaction Reports
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: '#999', textAlign: 'center' }}
                    >
                        Use the filters above to search for transaction reports
                        and
                        <br />
                        click "Search" to get started
                    </Typography>
                </Box>
            )}

            {hasSearched && freezeBlockReport.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    USER GIGYA UID
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    PROMOTION TYPE
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    BLOCK TIME (MT UTC)
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    BLOCK TYPE
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    BLOCK REASON
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    UNBLOCK TIME (MT UTC)
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {freezeBlockReport.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {row.gigyaId || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.eventType || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.blockTime
                                            ? dayjs(row.blockTime).format(
                                                  'YYYY-MM-DD HH:mm:ss'
                                              )
                                            : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.blockType ? (
                                            <Chip
                                                label={row.blockType.toUpperCase()}
                                                size="small"
                                                sx={{
                                                    ...getBlockTypeColor(
                                                        row.blockType
                                                    ),
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {row.blockReason || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.unblockTime
                                            ? dayjs(row.unblockTime).format(
                                                  'YYYY-MM-DD HH:mm:ss'
                                              )
                                            : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            )}

            {hasSearched && freezeBlockReport.length === 0 && (
                <Box sx={{ mt: 3, textAlign: 'center', color: '#666' }}>
                    No data found for the selected criteria.
                </Box>
            )}
        </div>
    );
};

export default FreezeBlockReport;
